// ExportClinicFeeDentist.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { getCrm, toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface ClinicFeeRow {
    dateFormat: string   // "DD/MM/YYYY"
    billNo: string
    branchCnNumber: string
    snCode?: string | null
    patientFullname: string
    doctorName: string
    list: string
    amountPaid: number
    amountCash: number
    amountTransfer: number
    amountDebit: number
    amountCredit: number
    amountTreatmentRight: number
    amountVoucher?: number
    amountPromotion?: number
    amountAdvance: number
    clinicFeeName?: string
    clinicFeePrice?: number
    dfPaid: number
    creditCost: number
    dfLostTreatment: number
    dfNet: number
}

export interface DayGroup {
    date: string         // "YYYY-MM-DD"
    table: ClinicFeeRow[]
}

export interface DfRow {
    doctorId: string
    totalAmount: number
    handInsurance: number
    amountPaid: number
    amountDf: number
    dfCredit: number
    dfLostTreatment: number
    dfLostCreditAndTreatment: number
    dfLabFee: number
    dfNet: number
}

export interface RemunerationClinicFeeData {
    rows: DayGroup[]
    rowLab: DayGroup[]    // empty ถ้าไม่มี LAB
    rowDf?: DfRow[]
}

export interface RemunerationClinicFeeReport {
    branchName: string
    dateStart: string    // "YYYY-MM-DD"
    dateEnd: string
    receiptPrefix: string
    doctorsFilter: string    // รวมชื่อหมอเป็น string เดียว
    labGroupName: string
    labCompanyName: string
    optionDfLabel: string    // e.g. "DF1"
    includeSn: boolean
    data: RemunerationClinicFeeData
    doctors: any,
    t: (key: string) => string
}

export async function ExportClinicFeeDentist(
    rpt: RemunerationClinicFeeReport
) {
    const hasCrm = getCrm()
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(rpt.t('REPORT.REPORT_NAME.REMUNERATION_CLINIC_FEE_ALL_DENTIST'), {
        pageSetup: { orientation: 'landscape' }
    })
    ws.properties.defaultRowHeight = 18

    // 1) กำหนด width A–U
    const widths = [
        8, 18, 20, 12, 12,              // A–D
        20, 35, 12, 12, 12, 12, 12, 12,   // F–M
        20, 12, 12, 12, 12, 12          // N–U
    ]
    if (!rpt.includeSn) widths.splice(4, 1)
    if (hasCrm) widths.splice(rpt.includeSn ? 13 : 12, 0, 12, 12)

    ws.columns = widths.map(w => ({ width: w }))

    // 2) Title row 1
    ws.mergeCells(1, 1, 1, widths.length)
    ws.getCell(1, 1).value = rpt.t('REPORT.REPORT_NAME.REMUNERATION_CLINIC_FEE_ALL_DENTIST')
    ws.getCell(1, 1).font = { size: 16, bold: true }
    ws.getCell(1, 1).alignment = { horizontal: 'center' }

    // 3) Filter row 2
    ws.mergeCells(2, 1, 2, widths.length)
    ws.getCell(2, 1).value = [
        `สาขา: ${rpt.branchName}`,
        `วันที่เริ่ม: ${toBuddhistYear(moment(rpt.dateStart), 'DD/MM/YYYY')}`,
        `ถึง: ${toBuddhistYear(moment(rpt.dateEnd), 'DD/MM/YYYY')}`,
        `ประเภทใบเสร็จ: ${rpt.receiptPrefix}`,
        `ทันตแพทย์: ${rpt.doctorsFilter}`,
        `LabGroup: ${rpt.labGroupName}`,
        `LabCompany: ${rpt.labCompanyName}`,
        // `เลือกการคัด DF: ${rpt.optionDfLabel}`
    ].join('    ')
    ws.getCell(2, 1).font = { size: 12 }
    ws.getCell(2, 1).alignment = { horizontal: 'left' }

    // 4) Header group row 3
    const cN = widths.length
    // 5) Leaf header row 4
    const hdr4 = [
        rpt.t('REPORT.TABLE.REMUNERATION.#NO'),
        rpt.t('REPORT.TABLE.REMUNERATION.BILL_ID'),
        rpt.t('REPORT.TABLE.REMUNERATION.DOCTOR'),
        rpt.t('REPORT.TABLE.REMUNERATION.PATIENT_CN_NUMBER'),
        rpt.t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE'),
        rpt.t('REPORT.TABLE.REMUNERATION.PATIENT_NAME'),
        rpt.t('REPORT.TABLE.REMUNERATION.ITEM'),
        rpt.t('REPORT.TABLE.REMUNERATION.PAYMENT'),
        rpt.t('REPORT.TABLE.REMUNERATION.CASH'),
        rpt.t('REPORT.TABLE.REMUNERATION.TRANSFER'),
        rpt.t('REPORT.TABLE.REMUNERATION.DEBIT'),
        rpt.t('REPORT.TABLE.REMUNERATION.CREDIT'),
        rpt.t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT'),
        ...(hasCrm ? [
            rpt.t('REPORT.TABLE.REMUNERATION.VOUCHER'),
            rpt.t('REPORT.TABLE.REMUNERATION.PROMOTION'),
        ] : []),
        rpt.t('REPORT.TABLE.REMUNERATION.ADVANCE'),
        rpt.t('REPORT.TABLE.REMUNERATION.NAME_CLINICFEE'),
        rpt.t('REPORT.TABLE.REMUNERATION.PRICE_CLINICFEE'),
        rpt.t('REPORT.TABLE.REMUNERATION.DF'),
        rpt.t('REPORT.TABLE.REMUNERATION.CREDIT_DF'),
        rpt.t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF'),
        rpt.t('REPORT.TABLE.REMUNERATION.NET_DF')
    ]

    if (!rpt.includeSn) hdr4.splice(4, 1)

    const r4 = ws.addRow(hdr4)
    r4.eachCell(cell => {
        cell.font = { bold: true }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
    })

    // 6) Data Clinic-Fee row 5+
    let ptr = 5
    if (!rpt.data.rows.length) {
        ws.mergeCells(ptr, 1, ptr, cN)
        ws.getCell(ptr, 1).value = rpt.t('REPORT.TITLE.NO_DATA')
        ws.getCell(ptr, 1).alignment = { horizontal: 'center' }
        ptr += 2
    } else {
        for (const grp of rpt.data.rows) {
            // date label
            ws.mergeCells(ptr, 1, ptr, cN)
            ws.getCell(ptr, 1).value = `วันที่ ${grp.table[0].dateFormat}`
            ws.getCell(ptr, 1).font = { bold: true }
            ws.getCell(ptr, 1).alignment = { horizontal: 'left' }
            ptr++

            // rows
            grp.table.forEach((d: any, i: any) => {
                const vals: any[] = [
                    i + 1,
                    d.billNo,
                    _.get(_.find(rpt.doctors, { dentistId: d.doctorId }), 'dentistFullname'),
                    d.branchCnNumber
                ]
                if (rpt.includeSn) vals.push(d.snCode || '-')
                vals.push(
                    d.patientFullname,
                    d.list,
                    d.amountPaid,
                    d.amountCash,
                    d.amountTransfer,
                    d.amountDebit,
                    d.amountCredit,
                    d.amountTreatmentRight,
                    ...(hasCrm ? [d.amountVoucher || 0, d.amountPromotion || 0] : []),
                    d.amountAdvance,
                    d.clinicFeeName || '-',
                    d.clinicFeePrice || 0,
                    d.dfPaid,
                    d.creditCost,
                    d.dfLostTreatment,
                    d.dfNet
                )
                ws.addRow(vals).eachCell((cell, col) => {
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    if (col >= (rpt.includeSn ? 8 : 7)) {
                        cell.numFmt = '#,##0.00'
                        cell.alignment = { horizontal: 'right' }
                    } else cell.alignment = { horizontal: 'left' }
                })
                ptr++
            })

            // subtotal
            const sums = grp.table.reduce((a, d) => {
                a.sp += d.amountPaid
                a.cash += d.amountCash
                a.tr += d.amountTransfer
                a.db += d.amountDebit
                a.cr += d.amountCredit
                a.trt += d.amountTreatmentRight
                a.vch += d.amountVoucher || 0
                a.prm += d.amountPromotion || 0
                a.adv += d.amountAdvance
                a.fee += d.clinicFeePrice || 0
                a.dfp += d.dfPaid
                a.crc += Number(d.creditCost) || 0
                a.dfl += d.dfLostTreatment
                a.dfn += d.dfNet
                return a
            }, { sp: 0, cash: 0, tr: 0, db: 0, cr: 0, trt: 0, vch: 0, prm: 0, adv: 0, fee: 0, dfp: 0, crc: 0, dfl: 0, dfn: 0 })
            const sumRow: (string | number)[] = []
            if (rpt.includeSn) sumRow.push('')
            sumRow.push('', '', '', '', '', rpt.t('REPORT.TITLE.SUM'),
                sums.sp, sums.cash, sums.tr, sums.db, sums.cr, sums.trt,
                ...(hasCrm ? [sums.vch, sums.prm] : []),
                sums.adv,
                '', sums.fee, sums.dfp, sums.crc, sums.dfl, sums.dfn
            )
            ws.addRow(sumRow).eachCell((cell, col) => {
                cell.font = { bold: true }
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                if (col >= (rpt.includeSn ? 8 : 7)) {
                    cell.numFmt = '#,##0.00'
                    cell.alignment = { horizontal: 'right' }
                } else cell.alignment = { horizontal: 'left' }
            })
            ptr += 2
        }
    }

    // 7) LAB Section
    ws.mergeCells(ptr, 1, ptr, cN)
    ws.getCell(ptr, 1).value = rpt.t('LAB Report')
    ws.getCell(ptr, 1).font = { size: 14, bold: true }
    ws.getCell(ptr, 1).alignment = { horizontal: 'left' }
    ptr++

    const labHdr = [
        rpt.t('REPORT.TABLE.REMUNERATION.#NO'),
        rpt.t('REPORT.TABLE.REMUNERATION.DOCTOR'),
        rpt.t('REPORT.TABLE.REMUNERATION.PATIENT_CN_NUMBER'),
        ...(rpt.includeSn ? [rpt.t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE')] : []),
        rpt.t('REPORT.TABLE.REMUNERATION.PATIENT_NAME'),
        rpt.t('REPORT.TABLE.REMUNERATION.DESCRIPTION'),
        rpt.t('REPORT.TABLE.REMUNERATION.LAB_NAME'),
        rpt.t('REPORT.TABLE.REMUNERATION.JOB'),
        'LAB Fee', 'DF LAB Fee'
    ]
    ws.addRow(labHdr).eachCell(cell => {
        cell.font = { bold: true }
        cell.alignment = { horizontal: 'center' }
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
    })
    ptr++

    if (!rpt.data.rowLab.length) {
        ws.mergeCells(ptr, 1, ptr, labHdr.length)
        ws.getCell(ptr, 1).value = rpt.t('REPORT.TITLE.NO_DATA')
        ws.getCell(ptr, 1).alignment = { horizontal: 'center' }
        ptr += 2
    } else {
        rpt.data.rowLab.forEach(gr => {
            ws.mergeCells(ptr, 1, ptr, labHdr.length)
            ws.getCell(ptr, 1).value = `วันที่ ${gr.date}`
            ws.getCell(ptr, 1).font = { bold: true }
            ws.getCell(ptr, 1).alignment = { horizontal: 'left' }
            ptr++
            gr.table.forEach((d: any, i: any) => {
                const vals: any[] = [i + 1, rpt.doctorsFilter, d.branchCnNumber]
                if (rpt.includeSn) vals.push(d.snCode || '-')
                vals.push(d.patientFullname, 'd.remark', d.lab, d.operativeName, d.labFee, d.dfLabFee)
                ws.addRow(vals).eachCell((cell, col) => {
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    if (col >= labHdr.length - 1) {
                        cell.numFmt = '#,##0.00'
                        cell.alignment = { horizontal: 'right' }
                    } else cell.alignment = { horizontal: 'left' }
                })
                ptr++
            })
            const sumL = gr.table.reduce((a: any, d: any) => (a + d.labFee), 0)
            const sumD = gr.table.reduce((a: any, d: any) => (a + d.dfLabFee), 0)
            const sr = Array(labHdr.length).fill('') as (string | number)[]
            sr[0] = rpt.t('REPORT.TITLE.SUM')
            sr[labHdr.length - 2] = sumL
            sr[labHdr.length - 1] = sumD
            ws.addRow(sr).eachCell((cell, col) => {
                cell.font = { bold: true }
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                if ([labHdr.length - 2, labHdr.length - 1].includes(col)) {
                    cell.numFmt = '#,##0.00'; cell.alignment = { horizontal: 'right' }
                } else cell.alignment = { horizontal: 'left' }
            })
            ptr += 2
        })
    }

    // 8) DF Section
    ws.mergeCells(ptr, 1, ptr, cN)
    ws.getCell(ptr, 1).value = rpt.t('REPORT.TABLE.REMUNERATION.DENTIST_FREE')
    ws.getCell(ptr, 1).font = { size: 14, bold: true }
    ws.getCell(ptr, 1).alignment = { horizontal: 'left' }
    ptr++

    const dfHdr = [
        rpt.t('REPORT.TABLE.REMUNERATION.#NO'),
        rpt.t('REPORT.TABLE.REMUNERATION.DOCTOR'),
        rpt.t('REPORT.TABLE.REMUNERATION.NET_PRICE'),
        rpt.t('REPORT.TABLE.REMUNERATION.SUM_PAID'),
        rpt.t('REPORT.TABLE.REMUNERATION.DF'),
        rpt.t('REPORT.TABLE.REMUNERATION.CREDIT_DF'),
        rpt.t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF'),
        rpt.t('REPORT.TABLE.REMUNERATION.SUM_DF'),
        'DF LAB', 'Net DF'
    ]
    ws.addRow(dfHdr).eachCell(cell => {
        cell.font = { bold: true }
        cell.alignment = { horizontal: 'center' }
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
    })
    ptr++

    if (!rpt.data.rowDf?.length) {
        ws.mergeCells(ptr, 1, ptr, dfHdr.length)
        ws.getCell(ptr, 1).value = rpt.t('REPORT.TITLE.NO_DATA')
        ws.getCell(ptr, 1).alignment = { horizontal: 'center' }
        ptr += 2
    } else {
        rpt.data.rowDf.forEach((d, i) => {
            const vals = [
                i + 1, _.get(_.find(rpt.doctors, { dentistId: Number(d.doctorId) }), 'dentistFullname'),
                d.totalAmount,
                d.amountPaid,
                d.amountDf,
                d.dfCredit,
                d.dfLostTreatment,
                d.dfLostCreditAndTreatment,
                d.dfLabFee,
                d.dfNet
            ]
            ws.addRow(vals).eachCell((cell, col) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                if (col >= 3) { cell.numFmt = '#,##0.00'; cell.alignment = { horizontal: 'right' } }
                else { cell.alignment = { horizontal: 'left' } }
            })
            ptr++
        })
        const sumsDf = rpt.data.rowDf.reduce((a, d) => {
            a.ta += d.totalAmount
            a.sp += d.amountPaid
            a.df += d.amountDf
            a.cr += d.dfCredit
            a.lt += d.dfLostTreatment
            a.sl += d.dfLostCreditAndTreatment
            a.lf += d.dfLabFee
            a.nf += d.dfNet
            return a
        }, { ta: 0, sp: 0, df: 0, cr: 0, lt: 0, sl: 0, lf: 0, nf: 0 })
        const sumDfRow = [
            '', rpt.t('REPORT.TITLE.SUM'),
            sumsDf.ta,
            sumsDf.sp,
            sumsDf.df,
            sumsDf.cr,
            sumsDf.lt,
            sumsDf.sl,
            sumsDf.lf,
            sumsDf.nf
        ]
        ws.addRow(sumDfRow).eachCell((cell, col) => {
            cell.font = { bold: true }
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
            if (col >= 3) { cell.numFmt = '#,##0.00'; cell.alignment = { horizontal: 'right' } }
            else { cell.alignment = { horizontal: 'left' } }
        })
        ptr += 2
    }

    // 9) Total Income Summary
    ws.mergeCells(ptr, 1, ptr, cN)
    ws.getCell(ptr, 1).value = rpt.t('Total Income Summary')
    ws.getCell(ptr, 1).font = { size: 14, bold: true }
    ws.getCell(ptr, 1).alignment = { horizontal: 'left' }
    ptr++

    const totHdr = [
        rpt.t('REPORT.TABLE.REMUNERATION.INCOME'),
        rpt.t('REPORT.TABLE.REMUNERATION.NET_CLINIC'),
        rpt.t('REPORT.TABLE.REMUNERATION.DF_NET'),
        rpt.t('REPORT.TABLE.REMUNERATION.DENTIST_FREE'),
        rpt.t('REPORT.TABLE.REMUNERATION.DF_LAB'),
        rpt.t('REPORT.TABLE.REMUNERATION.CASH'),
        rpt.t('REPORT.TABLE.REMUNERATION.TRANSFER'),
        rpt.t('REPORT.TABLE.REMUNERATION.DEBIT'),
        rpt.t('REPORT.TABLE.REMUNERATION.CREDIT'),
        rpt.t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT'),
        ...(hasCrm ? [
            rpt.t('REPORT.TABLE.REMUNERATION.VOUCHER'),
            rpt.t('REPORT.TABLE.REMUNERATION.PROMOTION'),
        ] : []),
        rpt.t('REPORT.TABLE.REMUNERATION.ADVANCE')
    ]
    ws.addRow(totHdr).eachCell(cell => {
        cell.font = { bold: true }
        cell.alignment = { horizontal: 'right' }
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
    })
    ptr++

    // คำนวณ totals
    const allRows = rpt.data.rows.flatMap(g => g.table)
    const income = allRows.reduce((s, d) => s + d.amountPaid, 0)
    const dfNetTot = rpt.data.rowDf?.reduce((s, d) => s + d.dfNet, 0) || 0
    const netClin = Math.max(0, income - dfNetTot)
    const cash = allRows.reduce((s, d) => s + d.amountCash, 0)
    const trf = allRows.reduce((s, d) => s + d.amountTransfer, 0)
    const dbt = allRows.reduce((s, d) => s + d.amountDebit, 0)
    const cred = allRows.reduce((s, d) => s + d.amountCredit, 0)
    const trtR = allRows.reduce((s, d) => s + d.amountTreatmentRight, 0)
    const vchT = allRows.reduce((s, d) => s + (d.amountVoucher || 0), 0)
    const prmT = allRows.reduce((s, d) => s + (d.amountPromotion || 0), 0)
    const adv = allRows.reduce((s, d) => s + d.amountAdvance, 0)
    const dfLabTot = rpt.data.rowDf?.reduce((s, d) => s + d.dfLabFee, 0) || 0

    const totalRow = [
        income, netClin, dfNetTot, dfNetTot, dfLabTot,
        cash, trf, dbt, cred, trtR,
        ...(hasCrm ? [vchT, prmT] : []),
        adv
    ]
    ws.addRow(totalRow).eachCell(cell => {
        cell.numFmt = '#,##0.00'
        cell.alignment = { horizontal: 'right' }
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
    })

    // 10) Save
    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(
        new Blob([buf]),
        `${rpt.t('REPORT.REPORT_NAME.REMUNERATION_CLINIC_FEE_ALL_DENTIST')} (${rpt.dateStart}-${rpt.dateEnd}).xlsx`
    )
}
